import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Phone, MessageCircle, KeyRound, ShieldCheck, Timer } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, GALLERY_ITEMS } from "@/siteConfig";

const lines = [
    { text: "مفاتيح سيارات", accent: false },
    { text: "وبرمجة ريموت", accent: false },
    { text: "في جدة", accent: true },
];

const Hero = () => {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 140, damping: 16 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 140, damping: 16 });
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 700], [0, 140]);

    const handleMouse = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-20"
        >
            <motion.div
                style={{ y: bgY }}
                className="animate-glow pointer-events-none absolute -top-32 -left-32 h-[34rem] w-[34rem] rounded-full bg-amber-500/15 blur-[130px]"
            />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,0.06),transparent_60%)]" />

            <div className="relative z-10 mx-auto grid w-full max-w-6xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        data-testid="hero-eyebrow"
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-bold tracking-wide text-amber-400"
                    >
                        <Timer size={14} />
                        خدمة طوارئ متنقلة — جدة
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-4xl font-black leading-[1.25] tracking-tight text-white sm:text-5xl lg:text-6xl"
                    >
                        {lines.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.accent ? "text-amber-400" : ""}`}
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.8 }}
                        data-testid="hero-tagline"
                        className="mt-5 text-lg font-extrabold text-amber-400 sm:text-xl"
                    >
                        نوصلك خلال دقائق — خدمة 24 ساعة
                    </motion.p>

                    <motion.p
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.9 }}
                        data-testid="hero-subtext"
                        className="mt-6 max-w-md text-base leading-relaxed text-gray-400 sm:text-lg"
                    >
                        برمجة مفاتيح السيارات بجميع أنواعها، وفتح الشقق والأبواب المقفلة
                        بدون أي إتلاف. فنيونا منتشرون في كل أحياء جدة على مدار الساعة.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 1.05 }}
                        className="mt-9 flex flex-col gap-4 sm:flex-row"
                    >
                        <a
                            href={PHONE_TEL}
                            data-testid="hero-call-button"
                            className="cta-shine flex items-center justify-center gap-3 rounded-2xl bg-blue-600 px-8 py-4 text-lg font-extrabold text-white shadow-[0_10px_36px_rgba(59,130,246,0.35)] transition-colors duration-300 hover:bg-blue-500"
                        >
                            <Phone size={22} />
                            <span>
                                اتصل الآن — <span dir="ltr">{PHONE_DISPLAY}</span>
                            </span>
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-button"
                            className="cta-shine flex items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-8 py-4 text-lg font-extrabold text-white shadow-[0_10px_36px_rgba(34,197,94,0.3)] transition-colors duration-300 hover:bg-emerald-500"
                        >
                            <MessageCircle size={22} />
                            محادثة واتساب جاهزة
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 1.2 }}
                    data-testid="hero-mobile-card"
                    className="relative overflow-hidden rounded-3xl border border-white/10 lg:hidden"
                >
                    <img
                        src={GALLERY_ITEMS[0].url}
                        alt="مفاتيح وريموتات سيارات"
                        className="h-44 w-full object-cover"
                        loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17]/80 via-transparent to-transparent" />
                    <span className="absolute bottom-3 right-3 flex items-center gap-2 rounded-full border border-white/10 bg-[#0B0F17]/80 px-3 py-1.5 text-[11px] font-bold text-amber-400 backdrop-blur-md">
                        <ShieldCheck size={13} />
                        فتح بدون إتلاف — وصول ١٥–٣٠ دقيقة
                    </span>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative hidden justify-center lg:flex"
                    style={{ perspective: 1100 }}
                    onMouseMove={handleMouse}
                    onMouseLeave={() => {
                        mx.set(0);
                        my.set(0);
                    }}
                >
                    <motion.div
                        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                        className="relative w-[24rem] overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 shadow-[0_40px_90px_rgba(0,0,0,0.55)] backdrop-blur-md"
                        data-testid="hero-key-card"
                    >
                        <img
                            src={GALLERY_ITEMS[0].url}
                            alt="برمجة مفتاح سيارة ذكي"
                            className="h-[28rem] w-full object-cover"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
                        <div
                            className="animate-float absolute top-5 right-5 flex items-center gap-2 rounded-full border border-white/15 bg-[#0B0F17]/70 px-4 py-2 text-xs font-bold text-amber-400 backdrop-blur-md"
                            style={{ transform: "translateZ(50px)" }}
                        >
                            <KeyRound size={14} />
                            برمجة · نسخ · فتح
                        </div>
                        <div
                            className="absolute bottom-5 right-5 left-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0B0F17]/75 p-4 backdrop-blur-md"
                            style={{ transform: "translateZ(35px)" }}
                        >
                            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-500/15 text-amber-400">
                                <ShieldCheck size={22} />
                            </span>
                            <div>
                                <p className="text-sm font-extrabold text-white">فتح بدون إتلاف</p>
                                <p className="text-xs text-gray-400">استجابة خلال ١٥–٣٠ دقيقة</p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
